// Sprint-16 — top-nav bell + dropdown trigger.
//
// Visual contract:
//   • Lucide <Bell /> in a square ghost button, sized to match the
//     adjacent ThemeSwitcher button.
//   • Red-dot-like badge in the top-right corner when unreadCount > 0.
//     Shows the count up to 9; "9+" beyond.
//   • Click toggles the InboxDropdown anchored beneath the bell.
//   • Outside-click + Escape close the dropdown (same pattern as
//     ThemeSwitcher in AppShell.tsx).
//
// Mount: place between the TrainingBucket badge and the user avatar in
// AppShell. The bell also surfaces the unwatched-team-captures count
// (watchStore.unwatchedCount) — a quiet dot on the icon when there are no
// unread notifications, plus an "N unwatched team captures" row at the top
// of the dropdown linking to /?filter=unwatched. (The old standalone top-nav
// "Unwatched · N" chip was removed 2026-06-01 to keep the header clean.)
//
// Polling: on mount we wire `attachNotificationPollers` which kicks off an
// immediate refresh + 60s interval + window-focus refresh. Teardown unwires
// on unmount so dev hot-reload doesn't pile up timers.

import { useEffect, useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Bell, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  useNotifications, attachNotificationPollers,
} from '../../stores/notificationsStore';
import { useWatch } from '../../stores/watchStore';
import { InboxDropdown } from './InboxDropdown';

export function InboxBell() {
  const { t } = useTranslation('console');
  // Individual selector — primitives only, never an object selector.
  const unread = useNotifications(s => s.unreadCount);
  // Unwatched team captures (2026-06-01) — surfaced here after the standalone
  // top-nav "Unwatched · N" chip was removed to keep the header clean. Same
  // source the old AppShell badge read from (watchStore.unwatchedCount).
  const unwatched = useWatch(s => s.unwatchedCount);

  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Start the pollers exactly once per mounted bell. AppShell currently
  // never unmounts during a session, but the cleanup matters for tests
  // and hot-reload.
  useEffect(() => attachNotificationPollers(), []);

  // Close on outside-click + Esc. Mirrors ThemeSwitcher's exact pattern so
  // the two dropdowns behave consistently for the user.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Cap displayed count at 9; switch to "9+" beyond. Anything >= 100 would
  // overflow the badge geometry; capping reads naturally.
  const badge = unread > 9 ? '9+' : String(unread);

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        className="ws-btn-ghost relative"
        aria-label={[
          unread > 0 ? t('inboxBell.unreadNotifications', { count: unread }) : null,
          unwatched > 0 ? t('inboxBell.unwatchedCaptures', { count: unwatched }) : null,
        ].filter(Boolean).join(', ') || t('inboxDropdown.notifications')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}>
        <Bell size={16} strokeWidth={1.75} aria-hidden="true" />
        {unread > 0 && (
          <span
            aria-hidden="true"
            className="absolute inline-flex items-center justify-center font-mono tabular-nums"
            style={{
              // Top-right corner of the icon. We position with a tiny
              // negative offset so the badge sits PROUD of the icon rather
              // than overlapping the glyph — easier to read at 11px.
              top: 2, right: 2,
              minWidth: 14, height: 14, padding: '0 3px',
              borderRadius: 999,
              background: 'var(--ws-accent)',
              color: 'var(--ws-bg)',
              fontSize: 9.5, lineHeight: 1,
              border: '1.5px solid var(--ws-nav-bg, var(--ws-bg))',
            }}>
            {badge}
          </span>
        )}
        {/* When there are unwatched team captures but NO unread notifications,
            the count badge above is absent — show a quiet dot so the bell still
            signals "there's something for you" without inventing a number. */}
        {unread === 0 && unwatched > 0 && (
          <span
            aria-hidden="true"
            className="absolute"
            style={{
              top: 3, right: 3,
              width: 7, height: 7,
              borderRadius: 999,
              background: 'var(--ws-accent)',
              border: '1.5px solid var(--ws-nav-bg, var(--ws-bg))',
            }}
          />
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 top-full z-50"
          // Zero-height anchor so the unwatched row + InboxDropdown (which
          // anchors itself to `top-full`) stack cleanly without overlapping.
          // The dropdown keeps owning its own positioning + width.
        >
          {unwatched > 0 && (
            <Link
              to="/?filter=unwatched"
              onClick={() => setOpen(false)}
              title={t('inboxBell.unwatchedLinkTitle', { count: unwatched })}
              className="flex items-center gap-2 w-[320px] rounded-md px-3 py-2.5 ws-anim-fade-up"
              style={{
                background: 'var(--ws-surface)',
                border: '1px solid var(--ws-border)',
                boxShadow: 'var(--ws-shadow-lg)',
                color: 'var(--ws-ink-2)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--ws-ink)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--ws-ink-2)'; }}>
              <Eye size={14} strokeWidth={1.75} aria-hidden="true" />
              <span className="text-[12px]">
                <Trans
                  i18nKey="console:inboxBell.unwatchedLinkText"
                  count={unwatched}
                  components={{ num: <span className="font-mono tabular-nums" /> }}
                />
              </span>
              <span className="ml-auto text-[12px]" aria-hidden="true">→</span>
            </Link>
          )}
          {/* Relative wrapper: the dropdown's own `top-full` resolves to the
              bottom of this zero-height block, so it sits directly under the
              unwatched row (or under the bell when there are no captures). */}
          <div className="relative">
            <InboxDropdown onClose={() => setOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
}
